/*
 * RBAC Feature (Permission) Codes — Phase 1 (Preparation)
 * ------------------------------------------------------------------
 * This file is the single source of truth for the permission strings that the
 * frontend will eventually check against the logged-in user's permission list
 * returned by the backend.
 *
 * IMPORTANT (Phase 1):
 *  - This is preparation only. Nothing here changes current application
 *    behavior. These codes are NOT yet enforced anywhere in the UI.
 *  - The codes below are PROPOSED and must be finalized with the backend team
 *    so both repositories share an identical permission model.
 *  - Convention: "<module>.<action>" in lower snake/dot case, e.g. "employee.edit".
 *
 * Do not hardcode raw permission strings in components. Import from here so a
 * rename only happens in one place.
 */

/** Application modules (maps to backend "Application Modules" entity). */
export const MODULES = Object.freeze({
  DASHBOARD: "dashboard",
  EMPLOYEE: "employee",
  LEAVE: "leave",
  REIMBURSEMENT: "reimbursement",
  SALARY: "salary",
  PAYSLIP: "payslip",
  HIRING: "hiring",
  POLICY: "policy",
  FORM16: "form16",
  RESOURCE: "resource",
  EXPENDITURE: "expenditure",
  PROJECT: "project",
  CLIENT: "client",
  INVOICE: "invoice",
  REVENUE: "revenue",
  WORKLOG: "worklog",
  EMAIL_TEMPLATE: "email_template",
  SETTINGS: "settings",
  TENANT: "tenant",
  PROFILE: "profile",
  SKILL: "skill",
  NOTIFICATION: "notification",
});

/**
 * Feature (permission) codes grouped by module.
 * Every action the UI currently exposes (view/create/edit/delete/import/export/
 * approve/etc.) has a proposed code here.
 */
export const FEATURE_CODES = Object.freeze({
  // Dashboard / Home
  DASHBOARD_VIEW: "dashboard.view",
  DASHBOARD_HOLIDAY_CREATE: "dashboard.holiday.create",
  DASHBOARD_HOLIDAY_DELETE: "dashboard.holiday.delete",
  DASHBOARD_EVENT_CREATE: "dashboard.event.create",
  DASHBOARD_EVENT_DELETE: "dashboard.event.delete",

  // Employee
  EMPLOYEE_VIEW: "employee.view",
  EMPLOYEE_CREATE: "employee.create",
  EMPLOYEE_EDIT: "employee.edit",
  EMPLOYEE_DELETE: "employee.delete",
  EMPLOYEE_IMPORT: "employee.import",
  EMPLOYEE_EXPORT: "employee.export",
  EMPLOYEE_SALARY_VIEW: "employee.salary.view", // salary field visibility in add/edit forms
  EMPLOYEE_ROLE_ASSIGN_ADMIN: "employee.role.assign_admin", // "Admin" option in role select
  // Employee Details page — individual tabs
  EMPLOYEE_OVERVIEW_VIEW: "employee.overview.view", // "Overview" tab
  EMPLOYEE_AADHAR_VIEW: "employee.aadhar.view", // "Aadhar Card" tab
  EMPLOYEE_PAN_VIEW: "employee.pan.view", // "PAN Card" tab
  // Employees list page — individual tabs
  EMPLOYEE_ACTIVE_LIST_VIEW: "employee.active_list.view", // "Active" tab
  EMPLOYEE_RELEASED_LIST_VIEW: "employee.released_list.view", // "Released" tab

  // Leave Management — per the approved RBAC audit (docs), these codes are
  // final and match the seed plan exactly; do not rename.
  LEAVE_VIEW_MODULE: "leave.view_module", // can open the Leave Management page at all
  LEAVE_VIEW_OWN_HISTORY: "leave.view_own_history", // "Your History" tab
  LEAVE_APPLY: "leave.apply", // Apply Leave form
  LEAVE_CANCEL: "leave.cancel", // cancel own pending leave request
  LEAVE_REQUESTS_VIEW: "leave.requests.view", // "Requests" tab (pending leave grid)
  LEAVE_REQUESTS_APPROVE: "leave.requests.approve",
  LEAVE_REQUESTS_REJECT: "leave.requests.reject",
  LEAVE_ALL_HISTORY_VIEW: "leave.all_history.view", // "All History" tab
  LEAVE_ALL_HISTORY_EXPORT: "leave.all_history.export",
  LEAVE_SUMMARY_VIEW: "leave.summary.view", // "Summary" tab
  LEAVE_OVERVIEW_VIEW: "leave.overview.view", // /LeavesOverview page

  // Reimbursement / Extra Work
  REIMBURSEMENT_VIEW: "reimbursement.view", // own history
  REIMBURSEMENT_CREATE: "reimbursement.create", // apply extra work
  // "Requests" tab (pending extra-work grid) and its two actions. Not yet
  // seeded as real backend features — see REIMBURSEMENT_REQUESTS_VIEW /
  // REIMBURSEMENT_REJECT usage in Reimbursement/index.js for the fallback
  // that keeps existing REIMBURSEMENT_APPROVE-only roles working until the
  // backend grants these two independently (mirrors LEAVE_REQUESTS_VIEW/
  // APPROVE/REJECT).
  REIMBURSEMENT_REQUESTS_VIEW: "reimbursement.requests.view",
  REIMBURSEMENT_APPROVE: "reimbursement.approve",
  REIMBURSEMENT_REJECT: "reimbursement.reject",
  REIMBURSEMENT_VIEW_ALL: "reimbursement.view_all",

  // Salary Management
  SALARY_VIEW: "salary.view",
  SALARY_INCENTIVE_ADD: "salary.incentive.add",
  SALARY_DEDUCTION_ADD: "salary.deduction.add",
  SALARY_PAYROLL_GENERATE: "salary.payroll.generate",
  SALARY_PAYSLIP_GENERATE: "salary.payslip.generate",
  SALARY_PAYSLIP_REGENERATE: "salary.payslip.regenerate",
  SALARY_AUDIT_GENERATE: "salary.audit.generate",
  SALARY_LEAVES_IMPORT: "salary.leaves.import",
  SALARY_LEAVES_EXPORT: "salary.leaves.export",

  // Payslip
  PAYSLIP_VIEW: "payslip.view", // own payslip
  PAYSLIP_VIEW_ANY: "payslip.view_any", // recent payslips of any employee

  // Hiring
  HIRING_VIEW: "hiring.view",
  HIRING_JOB_CREATE: "hiring.job.create",
  HIRING_JOB_EDIT: "hiring.job.edit",
  HIRING_JOB_DELETE: "hiring.job.delete",
  HIRING_JOB_STATUS_CHANGE: "hiring.job.status_change", // close / reopen / publish
  HIRING_CANDIDATE_VIEW: "hiring.candidate.view",
  HIRING_CANDIDATE_CREATE: "hiring.candidate.create",
  HIRING_CANDIDATE_EDIT: "hiring.candidate.edit",
  HIRING_CANDIDATE_DELETE: "hiring.candidate.delete",
  HIRING_CANDIDATE_STATUS_CHANGE: "hiring.candidate.status_change",
  HIRING_RESUME_IMPORT: "hiring.resume.import",
  HIRING_RESUME_DOWNLOAD: "hiring.resume.download",

  // Policies
  POLICY_VIEW: "policy.view",
  POLICY_CREATE: "policy.create",
  POLICY_EDIT: "policy.edit",
  POLICY_DELETE: "policy.delete",
  POLICY_REQUEST_APPROVE: "policy.request.approve",
  POLICY_REQUEST_REJECT: "policy.request.reject",
  POLICY_REQUEST_VIEW: "policy.request.view", // "Requests" tab visibility
  POLICY_REQUEST_PDF_VIEW: "policy.request.pdf_view", // "View" button on a request row (opens the policy PDF) — distinct from the tab-visibility permission above; display name is just "View", set on the backend feature seed
  POLICY_HISTORY_VIEW: "policy.history.view", // "History" tab visibility
  POLICY_HISTORY_PDF_VIEW: "policy.history.pdf_view", // "View" button on a history row (opens the policy PDF) — distinct from the tab-visibility permission above; display name is just "View", set on the backend feature seed

  // Form 16
  FORM16_VIEW: "form16.view",
  FORM16_UPLOAD: "form16.upload",
  FORM16_EDIT: "form16.edit",
  FORM16_DELETE: "form16.delete",
  FORM16_DOWNLOAD: "form16.download",
  FORM16_DOWNLOAD_ALL: "form16.download_all",

  // Resource Tracking
  RESOURCE_VIEW: "resource.view",
  RESOURCE_CREATE: "resource.create",
  RESOURCE_ASSIGN: "resource.assign",
  RESOURCE_RELEASE: "resource.release",
  RESOURCE_IMPORT: "resource.import",
  RESOURCE_EXPORT: "resource.export",
  RESOURCE_SUMMARY_VIEW: "resource.summary.view",
  RESOURCE_TRACKING_VIEW: "resource.tracking.view",

  // Expenditure
  EXPENDITURE_VIEW: "expenditure.view",
  EXPENDITURE_CREATE: "expenditure.create", // upload bill
  EXPENDITURE_DELETE: "expenditure.delete",
  EXPENDITURE_EXPORT: "expenditure.export",
  EXPENDITURE_BILL_DOWNLOAD: "expenditure.bill.download",

  // Projects
  PROJECT_VIEW: "project.view",
  PROJECT_CREATE: "project.create",
  PROJECT_EDIT: "project.edit",
  PROJECT_DELETE: "project.delete",
  PROJECT_CLONE: "project.clone",
  PROJECT_EMPLOYEE_ASSIGN: "project.employee.assign",
  PROJECT_EMPLOYEE_EDIT: "project.employee.edit",
  PROJECT_EMPLOYEE_RELEASE: "project.employee.release",
  PROJECT_EMPLOYEE_REMOVE: "project.employee.remove",
  PROJECT_INVOICE_DOWNLOAD: "project.invoice.download",

  // Clients
  CLIENT_VIEW: "client.view",
  CLIENT_CREATE: "client.create",
  CLIENT_EDIT: "client.edit",
  CLIENT_DELETE: "client.delete",

  // Invoices
  INVOICE_VIEW: "invoice.view",
  INVOICE_EDIT: "invoice.edit",
  INVOICE_STRIKE_OFF: "invoice.strike_off",
  INVOICE_SEND_EMAIL: "invoice.send_email",
  INVOICE_DOWNLOAD: "invoice.download",
  INVOICE_DOWNLOAD_ALL: "invoice.download_all",
  INVOICE_IMPORT: "invoice.import",
  INVOICE_EXPORT: "invoice.export",
  INVOICE_SEQUENCE_EDIT: "invoice.sequence.edit",

  // Revenue
  REVENUE_VIEW: "revenue.view",
  REVENUE_PROJECT_EXPORT: "revenue.project.export",
  REVENUE_EMPLOYEE_EXPORT: "revenue.employee.export",

  // WorkLogs
  WORKLOG_VIEW: "worklog.view", // own worklogs
  WORKLOG_CREATE: "worklog.create",
  WORKLOG_EDIT: "worklog.edit",
  WORKLOG_DELETE: "worklog.delete",
  WORKLOG_EXPORT: "worklog.export",
  WORKLOG_VIEW_ALL: "worklog.view_all", // admin/lead project dashboards

  // Email Templates
  EMAIL_TEMPLATE_VIEW: "email_template.view",
  EMAIL_TEMPLATE_CREATE: "email_template.create",
  EMAIL_TEMPLATE_EDIT: "email_template.edit",
  EMAIL_TEMPLATE_DELETE: "email_template.delete",

  // Settings
  SETTINGS_VIEW: "settings.view",
  SETTINGS_EDIT: "settings.edit",
  SETTINGS_BILLING_VIEW: "settings.billing.view",
  SETTINGS_BILLING_EDIT: "settings.billing.edit",

  // Tenants
  TENANT_VIEW: "tenant.view",
  TENANT_CREATE: "tenant.create",
  TENANT_EDIT: "tenant.edit",
  TENANT_DELETE: "tenant.delete",

  // Profile (self-service)
  PROFILE_VIEW: "profile.view",
  PROFILE_EDIT: "profile.edit",
  PROFILE_LEAD_ASSIGN: "profile.lead.assign",

  // Skills
  SKILL_VIEW: "skill.view",
  SKILL_CREATE: "skill.create",
  SKILL_EDIT: "skill.edit",
  SKILL_DELETE: "skill.delete",
});

/**
 * Optional module-level "view" code lookup. Useful when gating a whole route or
 * nav item by a single top-level permission.
 */
export const MODULE_VIEW_PERMISSION = Object.freeze({
  [MODULES.DASHBOARD]: FEATURE_CODES.DASHBOARD_VIEW,
  [MODULES.EMPLOYEE]: FEATURE_CODES.EMPLOYEE_VIEW,
  [MODULES.LEAVE]: FEATURE_CODES.LEAVE_VIEW_MODULE,
  [MODULES.REIMBURSEMENT]: FEATURE_CODES.REIMBURSEMENT_VIEW,
  [MODULES.SALARY]: FEATURE_CODES.SALARY_VIEW,
  [MODULES.PAYSLIP]: FEATURE_CODES.PAYSLIP_VIEW,
  [MODULES.HIRING]: FEATURE_CODES.HIRING_VIEW,
  [MODULES.POLICY]: FEATURE_CODES.POLICY_VIEW,
  [MODULES.FORM16]: FEATURE_CODES.FORM16_VIEW,
  [MODULES.RESOURCE]: FEATURE_CODES.RESOURCE_VIEW,
  [MODULES.EXPENDITURE]: FEATURE_CODES.EXPENDITURE_VIEW,
  [MODULES.PROJECT]: FEATURE_CODES.PROJECT_VIEW,
  [MODULES.CLIENT]: FEATURE_CODES.CLIENT_VIEW,
  [MODULES.INVOICE]: FEATURE_CODES.INVOICE_VIEW,
  [MODULES.REVENUE]: FEATURE_CODES.REVENUE_VIEW,
  [MODULES.WORKLOG]: FEATURE_CODES.WORKLOG_VIEW,
  [MODULES.EMAIL_TEMPLATE]: FEATURE_CODES.EMAIL_TEMPLATE_VIEW,
  [MODULES.SETTINGS]: FEATURE_CODES.SETTINGS_VIEW,
  [MODULES.TENANT]: FEATURE_CODES.TENANT_VIEW,
  [MODULES.PROFILE]: FEATURE_CODES.PROFILE_VIEW,
  [MODULES.SKILL]: FEATURE_CODES.SKILL_VIEW,
});

/**
 * The configurable Hiring permissions. Holding ANY one of these means the role
 * gets the HR Hiring management console (/hiring). A role with only the default
 * Hiring access (hiring.view / hiring.candidate.create / hiring.resume.download,
 * which every role inherits) gets the employee-facing job-openings screen
 * (/hiring/openings) instead. Mirrors the "Hiring management access" switch in
 * the Role Permissions editor.
 */
export const HIRING_MANAGEMENT_FEATURE_CODES = Object.freeze([
  FEATURE_CODES.HIRING_JOB_CREATE,
  FEATURE_CODES.HIRING_JOB_EDIT,
  FEATURE_CODES.HIRING_JOB_DELETE,
  FEATURE_CODES.HIRING_JOB_STATUS_CHANGE,
  FEATURE_CODES.HIRING_CANDIDATE_VIEW,
  FEATURE_CODES.HIRING_CANDIDATE_EDIT,
  FEATURE_CODES.HIRING_CANDIDATE_DELETE,
  FEATURE_CODES.HIRING_CANDIDATE_STATUS_CHANGE,
  FEATURE_CODES.HIRING_RESUME_IMPORT,
]);

/** Flat list of every proposed permission code (handy for tests/validation). */
export const ALL_FEATURE_CODES = Object.freeze(Object.values(FEATURE_CODES));
